/**
 * Client-side proof-of-work solver using Web Crypto API.
 * Finds a nonce such that SHA-256(challenge + nonce) has `difficulty` leading zero bits.
 */
export async function solveChallenge(
  challenge: string,
  difficulty: number
): Promise<string> {
  const encoder = new TextEncoder();
  const requiredBytes = Math.floor(difficulty / 8);
  const remainingBits = difficulty % 8;
  const mask = remainingBits > 0 ? 0xff << (8 - remainingBits) : 0;

  let nonce = 0;

  // Process in batches to avoid blocking the UI thread
  const BATCH_SIZE = 1000;

  while (true) {
    for (let i = 0; i < BATCH_SIZE; i++) {
      const data = encoder.encode(challenge + nonce.toString());
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hash = new Uint8Array(hashBuffer);

      let valid = true;
      for (let j = 0; j < requiredBytes; j++) {
        if (hash[j] !== 0) {
          valid = false;
          break;
        }
      }
      if (valid && remainingBits > 0) {
        if ((hash[requiredBytes]! & mask) !== 0) valid = false;
      }

      if (valid) return nonce.toString();
      nonce++;
    }

    // Yield to the event loop between batches so the UI stays responsive
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}
